import React, {useContext, useEffect, useState} from 'react';
import {Card, CardBody, CardHeader, Col, Container, Modal, ModalBody, ModalFooter, ModalHeader, Row} from 'reactstrap';
import {Badges, Breadcrumbs, Btn, H5} from '../../../../AbstractElements';
import DataTable from "react-data-table-component";
import axiosConfig from "../../../../Config/axios";
import IconBtn from "../../../../CommonElements/IconButton";
import ButtonIcon from "../../../../CommonElements/ButtonIcon";
import UserForm from "../Component/Form";
import Loader from "../../../../Layout/Loader";
import {getPrivilegeRoute} from "../../../../Route/RouteIndex";
import SearchOptions from "../../../CommonComponents/Search";
import AuthorizationContext from "../../../../_helper/Authorization";
import {User} from "../Types/UserTypes";
import type {AuthorizationContextType} from "../../../../_helper/Authorization/AuthorizationTypes";

const UserIndex = () => {
    const [loaderVisibility, setLoaderVisibility] = useState<boolean>(false);
    const [users, setUsers] = useState<User[]>([]);
    const [userModalVisibility, setUserModalVisibility] = useState<boolean>(false);
    const [searchVisibility, setSearchVisibility] = useState<boolean>(false);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [perPage, setPerPage] = useState<number>(10);
    const [totalRows, setTotalRows] = useState<number>(0);
    const searchOptions = [{name: 'name'}, {name: 'email'}];
    const authContext = useContext(AuthorizationContext) as AuthorizationContextType;
    const {errorHandler, hasPermission} = authContext || {};

    const userModalToggle = () => {
        setUserModalVisibility(!userModalVisibility);
    }

    const getUsers = (params: Record<string, unknown>, page: number = currentPage, limit: number = perPage) => {
        setLoaderVisibility(true);
        const config = {
            params: {...params, with: ['roles'], page, per_page: limit}
        };
        axiosConfig
            .get(`/privilege/user`, config)
            .then((response: any) => {
                if (response?.data?.data?.data) {
                    setUsers(response.data.data.data);
                }
                if (response?.data?.data?.meta) {
                    setCurrentPage(response.data.data.meta.current_page);
                    setPerPage(response.data.data.meta.per_page);
                    setTotalRows(response.data.data.meta.total);
                }
                setLoaderVisibility(false);
            })
            .catch((exception: any) => {
                errorHandler(exception);
                setLoaderVisibility(false);
            });
    }

    const deleteUser = (id: number) => {
        setLoaderVisibility(true);
        axiosConfig
            .delete(`/privilege/user/${id}`)
            .then((response: { data: { data: { id: number; }; }; }) => {
                if (response?.data?.data) {
                    setUsers(current => current.filter(user => {
                        return user.id !== response?.data?.data?.id;
                    }),);
                }
                setLoaderVisibility(false);
            })
            .catch((exception: any) => {
                errorHandler(exception);
                setLoaderVisibility(false);
            });
    }

    const addUser = (user: User) => {
        const usersList = [...users];
        usersList.push(user);
        setUsers(usersList);
        userModalToggle();
    }

    useEffect(() => {
        getUsers({}, 1, perPage);
    }, [])

    const userTableColumns = [
        {name: 'Name', selector: (row: User) => row.name, sortable: false,},
        {name: 'Email', selector: (row: User) => row.email, sortable: true, center: true,},
        {
            name: 'Role',
            cell: (row: User) =>
                <div className='d-flex align-items-right gap-2'>
                    {/* Display System Roles */}
                    {row.system_roles?.map((systemRole, index) => {
                        let badgeColor = 'primary';
                        if (systemRole === 'super-admin') badgeColor = 'danger';
                        if (systemRole === 'admin') badgeColor = 'warning';
                        if (systemRole === 'collector') badgeColor = 'success';
                        if (systemRole === 'participant') badgeColor = 'info';

                        return <Badges key={`system-${index}`} attrBadge={{
                            className: 'badge rounded-pill',
                            color: badgeColor,
                            pill: true
                        }}>{systemRole}</Badges>
                    })}
                    {/* Display Regular Roles */}
                    {row.roles?.map((role, index) => {
                        return <Badges key={`role-${index}`} attrBadge={{
                            className: 'badge rounded-pill',
                            color: 'primary',
                            pill: true
                        }}>{role.name}</Badges>
                    })}
                </div>,
            sortable: true, center: true,
        },
        {
            name: 'Option',
            cell: (row: User) => <div className='d-flex align-items-right gap-2'>
                {hasPermission && hasPermission('read-user') && (
                    <IconBtn link={`${getPrivilegeRoute('user')}/${row.id}`} type={'view'}/>
                )}
                {!row.system_defined && hasPermission && hasPermission('delete-user') && (
                    <IconBtn confirm={true} onclick={() => deleteUser(row.id)} type={'delete'}/>
                )}
            </div>,
            sortable: false,
            right: true,
        }];

    return (
        <div>
            <Breadcrumbs mainTitle="Users" parent="Privilege" title="Users"/>
            <Container fluid={true}>
                <Row>
                    <Col sm="12">
                        <Card>
                            <Loader visibility={loaderVisibility}/>
                            <CardHeader className='d-flex align-items-center justify-content-between'>
                                <H5>Users</H5>
                                <div>
                                    <div className='d-flex align-items-center gap-2'>
                                        {hasPermission && hasPermission('create-user') && (
                                            <ButtonIcon attrBtn={{
                                                onClick: () => setUserModalVisibility(true)
                                            }} type={'add'}/>
                                        )}

                                        {hasPermission && hasPermission('read-user') && (
                                            <ButtonIcon attrBtn={{
                                                onClick: () => getUsers({}, currentPage, perPage)
                                            }} type={'sync'}/>
                                        )}

                                        {hasPermission && hasPermission('read-user') && (
                                            <ButtonIcon attrBtn={{
                                                onClick: () => setSearchVisibility(!searchVisibility)
                                            }} type={'search'}/>
                                        )}
                                    </div>
                                </div>
                            </CardHeader>
                            <CardBody>
                                {searchVisibility && <SearchOptions callBack={(params: Record<string, unknown>) => getUsers(params, 1, perPage)}
                                                                    searchOptions={searchOptions}/>}
                                <DataTable pagination paginationServer columns={userTableColumns} data={users}
                                           paginationTotalRows={totalRows}
                                           paginationPerPage={perPage}
                                           paginationDefaultPage={currentPage}
                                           onChangePage={(page) => getUsers({}, page, perPage)}
                                           onChangeRowsPerPage={(newPerPage, page) => getUsers({}, page, newPerPage)}
                                           highlightOnHover={true}
                                           striped={true} responsive={true}/>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>
            <Modal isOpen={userModalVisibility} toggle={userModalToggle} centered>
                <ModalHeader>
                    Create User
                </ModalHeader>
                <ModalBody>
                    <UserForm callBack={addUser}/>
                </ModalBody>
                <ModalFooter>
                    <Btn attrBtn={{color: 'primary', onClick: userModalToggle}}>Close</Btn>
                </ModalFooter>
            </Modal>
        </div>
    );
};

export default UserIndex;